import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../context/AuthContext';
import { getUserPreferences, updateUserPreferences } from '../../../../firebase/services';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission
} from '../../../../utils/notificationUtils';
import ToggleSwitch from '../../../common/ToggleSwitch';
import './settings.css';

const NotificationPreferencesScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [emailUpdatesOptIn, setEmailUpdatesOptIn] = useState(false);
  const [goalRemindersEnabled, setGoalRemindersEnabled] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const pushSupported = isNotificationSupported();
  const pushPermission = getNotificationPermission();

  useEffect(() => {
    if (!currentUser) return;
    getUserPreferences(currentUser.uid)
      .then(prefs => {
        setEmailUpdatesOptIn(prefs.emailUpdatesOptIn);
        setGoalRemindersEnabled(prefs.goalRemindersEnabled || false);
      })
      .catch(err => {
        console.error('Failed to load notification preferences:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentUser]);

  const handleToggle = async (newValue) => {
    setEmailUpdatesOptIn(newValue);
    try {
      await updateUserPreferences(currentUser.uid, { emailUpdatesOptIn: newValue });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save notification preference:', err);
      setEmailUpdatesOptIn(!newValue);
    }
  };

  const handleGoalRemindersToggle = async (newValue) => {
    setGoalRemindersEnabled(newValue);
    try {
      if (newValue && pushPermission === 'default') {
        const success = await requestNotificationPermission(currentUser.uid);
        if (!success) {
          setGoalRemindersEnabled(false);
          return;
        }
      } else {
        await updateUserPreferences(currentUser.uid, { goalRemindersEnabled: newValue });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save goal reminders preference:', err);
      setGoalRemindersEnabled(!newValue);
    }
  };

  const goalToggleDisabled = !pushSupported || pushPermission === 'denied';

  return (
    <div className="settings-screen">
      <header className="settings-header">
        <button onClick={() => navigate('/profile')} className="back-button">
          &larr;
        </button>
        <h1>{t('notifications.title')}</h1>
      </header>
      <main className="settings-content" style={{ textAlign: 'left' }}>
        {!loading && (
          <>
            <div className="settings-pref-card">
              <div className="settings-pref-row">
                <div className="settings-pref-text">
                  <span className="settings-pref-label">{t('notifications.lightwayUpdates')}</span>
                  <span className="settings-pref-subtext">
                    {t('notifications.lightwayUpdatesDesc')}
                  </span>
                </div>
                <ToggleSwitch
                  isOn={emailUpdatesOptIn}
                  onToggle={handleToggle}
                />
              </div>

              <div className="settings-pref-row" style={{ marginTop: '16px' }}>
                <div className="settings-pref-text">
                  <span className="settings-pref-label">{t('notifications.goalReminders')}</span>
                  <span className="settings-pref-subtext">
                    {t('notifications.goalRemindersDesc')}
                  </span>
                  {!pushSupported && (
                    <span className="settings-pref-note-inline">
                      {t('notifications.notSupported')}
                    </span>
                  )}
                  {pushSupported && pushPermission === 'denied' && (
                    <span className="settings-pref-note-inline">
                      {t('notifications.blocked')}
                    </span>
                  )}
                </div>
                <ToggleSwitch
                  isOn={goalRemindersEnabled}
                  onToggle={handleGoalRemindersToggle}
                  disabled={goalToggleDisabled}
                />
              </div>

              {saved && (
                <p className="settings-pref-saved">{t('notifications.saved')}</p>
              )}
            </div>
            <p className="settings-pref-note">
              {t('notifications.futureNote')}
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default NotificationPreferencesScreen;
